import {
    initClient,
    setAssetDomain,
    thenightly,
} from '@news-mono/component-library'
import {
    authCheckAccessToken,
    ConsentLevel,
    loadUserLocation,
    retrieveLocationQueryString,
} from '@news-mono/web-common'

setAssetDomain()
// We need this to get the dependencies loaded in the correct order
require('../app/app')

declare global {
    interface Window {
        /** Refresh the authentication state, provided an access token. */
        refreshAuth?: (
            accessToken: string,
            version: string,
            platform: 'ios' | 'android',
            consent: ConsentLevel,
        ) => Promise<void>
        /** Logs out from the authentication state. */
        logoutAuth?: () => Promise<void>
        /** React Native Webview adapter. */
        ReactNativeWebView?: {
            postMessage: (data: string) => void
        }
    }
}

const { render, routeCacheRef } = initClient({
    version: { buildNumber: '', sha: '' },
    beforeInitialised: async ({ store, log, config }) => {
        if (window.location.search.indexOf('location') !== -1) {
            const region = retrieveLocationQueryString(window.location)
            store.dispatch(loadUserLocation(region))
        }

        await store.dispatch(
            authCheckAccessToken({
                onEvent: () => {},
                config,
                log,
                invocation: 'auto',
            }),
        )

        // notify app that it can update auth state.
        postWebviewMessage(true, store.getState().authentication.isLoggedIn)

        window.refreshAuth = async (
            accessToken,
            version,
            platform,
            consent,
        ) => {
            // Notify webview that it cannot refresh while already doing so.
            postWebviewMessage(
                false,
                store.getState().authentication.isLoggedIn,
            )
            await fetch(
                `/app/refresh?p=${platform}&version=${version}&c=${consent}`,
                {
                    method: 'POST',
                    body: JSON.stringify({ accessToken }),
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )

            await store.dispatch(
                authCheckAccessToken({
                    onEvent: () => {},
                    config,
                    log,
                    invocation: 'auto',
                    forceRefresh: true,
                }),
            )

            postWebviewMessage(true, store.getState().authentication.isLoggedIn)
        }

        window.logoutAuth = async () => {
            // Notify webview that it cannot refresh while logging out.
            postWebviewMessage(
                false,
                store.getState().authentication.isLoggedIn,
            )

            await fetch(`/app/logout`, {
                method: 'POST',
                credentials: 'include',
            })

            await store.dispatch(
                authCheckAccessToken({
                    onEvent: () => {},
                    config,
                    log,
                    invocation: 'auto',
                    forceRefresh: true,
                }),
            )

            postWebviewMessage(true, store.getState().authentication.isLoggedIn)
        }
    },
    requireApplication: () => require('../app/app').App,
    theme: thenightly,
})

if (module.hot) {
    module.hot.accept('../app/app', () => {
        if (routeCacheRef) {
            routeCacheRef.clear()
        }

        setTimeout(render)
    })
}

const postWebviewMessage = (canUpdateAuth: boolean, isLoggedIn: boolean) => {
    window.ReactNativeWebView?.postMessage(
        JSON.stringify({
            type: 'authentication-ready',
            canUpdateAuth,
            isLoggedIn,
        }),
    )
}
